import React, { useState } from 'react';
import { exploreService } from './exploreService';

const ShareLocationComponent = ({ latitude, longitude, placeName = '', address = '' }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateLinks = () => {
    const mapUrl = exploreService.generateMapUrl(latitude, longitude, 16, placeName);
    const shareUrl = exploreService.generateShareableLink(latitude, longitude, placeName);
    const staticMapUrl = exploreService.generateMapUrl(latitude, longitude, 15, placeName);
    
    return { mapUrl, shareUrl, staticMapUrl };
  };

  const handleShare = async () => {
    const { shareUrl } = generateLinks();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: placeName || 'Shared Location',
          text: address || `Location: ${latitude}, ${longitude}`,
          url: shareUrl
        });
      } catch (error) {
        console.log('Share cancelled:', error);
      }
    } else {
      setShowShareModal(true);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const { mapUrl, shareUrl, staticMapUrl } = generateLinks();

  return (
    <>
      <button
        onClick={handleShare}
        className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2"
      >
        <span>📤</span>
        <span>Share Location</span>
      </button>

      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Share Location</h3>
              <button 
                onClick={() => setShowShareModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Map Preview */}
              <div className="border rounded-lg overflow-hidden">
                <img 
                  src={staticMapUrl} 
                  alt="Location Map" 
                  className="w-full h-48 object-cover"
                />
              </div>

              {/* Location Info */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-1">
                  {placeName || 'Shared Location'}
                </h4>
                {address && (
                  <p className="text-sm text-gray-600 mb-2">{address}</p>
                )}
                <p className="text-xs text-gray-500">
                  📍 {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </p>
              </div>

              {/* Share URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shareable Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(shareUrl)}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => window.open(shareUrl, '_blank')}
                  className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <span>🗺️</span>
                  <span>Open in Maps</span>
                </button>
                
                <button
                  onClick={() => window.open(`https://maps.google.com/?q=${latitude},${longitude}`, '_blank')}
                  className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                >
                  <span>🔗</span>
                  <span>Google Maps</span>
                </button>
              </div>

              {/* QR Code Option */}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-3">Scan to open on mobile:</p>
                <div className="flex justify-center">
                  <div className="p-4 bg-white border rounded-lg">
                    {/* You can add a QR code generator here */}
                    <div className="w-32 h-32 bg-gray-100 flex items-center justify-center rounded">
                      <span className="text-gray-400">QR Code</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareLocationComponent;